import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, updateUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    if (attempts >= 10) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateUser(userResponse.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#050605] flex items-center justify-center p-6" data-testid="payment-success">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-[#c8b273] mx-auto mb-4 animate-spin" />
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
              Processing Payment
            </h1>
            <p className="text-[#8a948a]">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#22c55e] mx-auto mb-4" />
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
              Payment Successful!
            </h1>
            <p className="text-[#8a948a] mb-6">
              Thank you for your purchase. Your account has been updated.
            </p>
            <Link to="/dashboard">
              <Button className="btn-gold">
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#ff5e00]/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-[#ff5e00] text-3xl">!</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
              Session Expired
            </h1>
            <p className="text-[#8a948a] mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/subscription">
              <Button className="btn-gold">
                Try Again
              </Button>
            </Link>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#ff5e00]/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-[#ff5e00] text-3xl">?</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
              Status Unknown
            </h1>
            <p className="text-[#8a948a] mb-6">
              We couldn't confirm your payment status. Please check your account or contact support.
            </p>
            <Link to="/dashboard">
              <Button className="btn-gold">
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
